import { Component, OnInit, OnDestroy, ViewChild, ElementRef, HostListener } from '@angular/core';
import { AbstractFormComponent } from '@app/shared/common/abstract.component';
import { KycService } from '@app/core/kyc.service';
import { routerTransition } from '@app/router.animations';
import { QuestionBase } from '@app/core/models/question/question-base';
import { QuestionService } from '@app/core/question.service';
import { QuestionControlService } from '@app/core/question-control.service';
import { Router, ActivatedRoute } from '@angular/router';
import { NavTabService } from '@app/core/navTab.service';
import * as moment from 'moment';
import { getAgeFromDOB } from '@app/features/my-financial/my-financial';
import * as utils from '@app/core/utils';
import { environment } from 'environments/environment';

declare var $;
@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  animations: [routerTransition()],
  host: { '[@routerTransition]': '' }
})

export class ProfileComponent extends AbstractFormComponent implements OnInit, OnDestroy {
  questionnaire: QuestionBase<any>[];
  messageEmailInvalid: string;
  messageRequired: string;
  numberBoys: number;
  numberGirls: number;
  userProfile: any;
  dateString: string;
  dateLabel: string;
  isErrorDate: boolean;
  firstInvalidControlName = '';
  iconColor = "#2bcdb5";
  headerColor = "#4d4d4d";
  isQuickKyc = environment.isQuickKyc;
  isWhatsAppRegister = false;
  isSingpassProfile = false;
  @ViewChild('email', { read: ElementRef }) email: ElementRef;
  @ViewChild('gender', { read: ElementRef }) gender: ElementRef;
  @ViewChild('dob', { read: ElementRef }) dob: ElementRef;
  @ViewChild('marital', { read: ElementRef }) marital: ElementRef;
  @ViewChild('kids', { read: ElementRef }) kids: ElementRef;
  @ViewChild('smoked', { read: ElementRef }) smoked: ElementRef;

  constructor(private kycService: KycService,
    private questionService: QuestionService,
    private qcs: QuestionControlService,
    private router: Router,
    private route: ActivatedRoute,
    private navTabService: NavTabService) {
    super();
    this.questionnaire = questionService.buildProfileQuestionnaire(this.kycService.kyc.profile.getQuestion(this.isQuickKyc, this.kycService.isWhatsAppRegister));
  }

  private syncUserProfileFromForm() {
    const currentUserProfile = this.kycService.kyc.profile.userProfile || {} as any;
    this.kycService.kyc.profile.userProfile = {
      ...currentUserProfile,
      ...this.form.value,
      phone: currentUserProfile.phone
    };
  }

  ngOnInit(): void {
    let rs = this.kycService.kycInfo;
    this.iconColor = rs.IconColor || this.iconColor;
    this.headerColor = rs.HeaderColor || this.headerColor;
    this.isWhatsAppRegister = this.kycService.isWhatsAppRegister;
    this.isSingpassProfile = !!this.kycService.customerProfile;
    this.form = this.qcs.toFormGroup(this.questionnaire);
    this.userProfile = this.kycService.kyc.profile.userProfile;
    this.dateString = this.kycService.kyc.profile.dob;

    // ensure userProfile has expected shape to satisfy TS types
    if (!this.kycService.kyc.profile.userProfile) {
      this.kycService.kyc.profile.userProfile = {
        email: '',
        gender: '',
        age: 0,
        kids: 0,
        marital: 0,
        smoked: 0,
        countrycode: ''
      } as any;
      this.userProfile = this.kycService.kyc.profile.userProfile;
    }

    if (this.userProfile) {
      const keys = Object.keys(this.userProfile);
      keys.forEach(key => {
        if (this.form.controls[key]) {
          this.form.controls[key].setValue(this.userProfile[key]);
        }
      });
    }

    this.numberBoys = this.kycService.kyc.profile.quantityBoys;
    this.numberGirls = this.kycService.kyc.profile.quantityGirls;

    const sp = this.kycService.customerProfile || null;
    if (sp) {
      try {
        const formValue = this.form.value || {};
        this.kycService.kyc.profile.userProfile = this.kycService.kyc.profile.userProfile || {
          email: '', gender: '', age: 0, kids: 0, marital: 0, smoked: 0, countrycode: ''
        } as any;

        const firstName = sp.FirstName || (sp.FullName ? sp.FullName.trim().split(/\s+/).slice(1).join(' ') : '') || '';
        const lastName = sp.LastName || (sp.FullName ? sp.FullName.trim().split(/\s+/)[0] : '') || '';
        this.kycService.kyc.profile.userProfile.firstName = this.kycService.kyc.profile.userProfile.firstName || firstName;
        this.kycService.kyc.profile.userProfile.lastName = this.kycService.kyc.profile.userProfile.lastName || lastName;
        if (this.form.controls['FirstName']) this.form.controls['FirstName'].setValue(this.kycService.kyc.profile.userProfile.firstName);
        if (this.form.controls['LastName']) this.form.controls['LastName'].setValue(this.kycService.kyc.profile.userProfile.lastName);

        if (sp.Email) {
          this.kycService.kyc.profile.userProfile.email = sp.Email;
          if (this.form.controls['email']) {
            this.form.controls['email'].setValue(sp.Email);
            this.kycService.kyc.profile.emailInvalid = this.form.controls['email'] && this.form.controls['email'].valid;
          }
        }

        if (sp.DateOfBirth) {
          const dobMoment = moment(sp.DateOfBirth, 'YYYY-MM-DD', true).isValid()
            ? moment(sp.DateOfBirth, 'YYYY-MM-DD', true)
            : moment(sp.DateOfBirth);
          const dobString = dobMoment.format('YYYY-MM-DD');
          this.dateString = dobString;
          this.kycService.kyc.profile.dob = dobString;
          if (this.form.controls['age']) {
            this.form.controls['age'].setValue(getAgeFromDOB(dobString));
            this.form.updateValueAndValidity();
          }
        }

        if (sp.Nationality) {
          const nat = sp.Nationality;
          this.kycService.kyc.profile.userProfile.countrycode = nat;
          if (this.form.controls['countrycode']) {
            this.form.controls['countrycode'].setValue(nat);
          }
        }

        const mobile = (sp && (sp as any).MobileNo) || (sp && (sp as any).MobileNumber);
        if (mobile) {
          this.kycService.kyc.profile.userProfile.phone = mobile;
        }
        this.onClickNext();
      } catch (e) {
      }
    }
  }

  getDateChanged(event) {
    const formatDate = moment([Number(event.year), Number(event.month) - 1, Number(event.day)]).format('YYYY-MM-DD');
    this.kycService.kyc.profile.dob = formatDate;
    this.form.controls['age'].setValue(getAgeFromDOB(formatDate));
    this.form.updateValueAndValidity();
    this.isErrorDate = this.onErrorDate(formatDate)
  }

  onErrorDate(dob) {
    const todayDate = new Date(moment(new Date()).format('L'));
    const todayDOB = new Date(moment(new Date(dob)).format('L'));
    const dobTime = todayDOB.getTime();
    const todayTime = todayDate.getTime()
    return todayTime < dobTime
  }

  onKidChanged(event) {
    let quantityBoys = 0, quantityGirls = 0;
    if (event.kidBoys || event.kidGirls) {
      for (let index = 0; index < event.kidBoys.length; index++) {
        if (event.kidBoys[index].checked) {
          quantityBoys++;
        }
        if (event.kidGirls[index].checked) {
          quantityGirls++;
        }
      }
    }

    this.kycService.kyc.profile.quantityBoys = quantityBoys;
    this.kycService.kyc.profile.quantityGirls = quantityGirls;
    this.form.controls['kids'].setValue(quantityBoys + quantityGirls);
    this.form.updateValueAndValidity();
  }

  onClickNext() {
    utils.markFormGroupTouched(this.form);
    if (this.form.valid && !this.isErrorDate) {
      if (!this.kycService.kyc.profile.dob && this.form.controls['age']) {
        const date = moment().add(-38, 'year').toString();
        this.kycService.kyc.profile.dob = date;
        this.form.controls['age'].setValue(getAgeFromDOB(date));
      }

      this.kycService.kyc.profile.completed = true;
      this.syncUserProfileFromForm();
      this.bypassEmailValidation();
      this.navTabService.setActiveTab('submit');
      let tenant = this.kycService.getTenant();
      this.router.navigateByUrl('/' + tenant + '/journey/submit');
    } else {
      this.kycService.kyc.profile.completed = false;
      this.firstInvalidControlName = utils.findFirstInvalidControlName(this.form);
      this.scrollTo(this.firstInvalidControlName);
    }
  }

  scrollTo(key: string) {
    const currentScrollPos = document.getElementById('custom-scrollbar');
    switch (key) {
      case 'email':
        this.scrollToElement(currentScrollPos, this.email.nativeElement);
        break;
      case 'age':
        this.scrollToElement(currentScrollPos, this.dob.nativeElement);
        break;
      case 'gender':
        this.scrollToElement(currentScrollPos, this.gender.nativeElement);
        break;
      default:
        break;
    }
  }

  ngOnDestroy() {
    utils.markFormGroupTouched(this.form);
    if (this.form.valid) {
      this.kycService.kyc.profile.completed = true;
      this.syncUserProfileFromForm();
    } else {
      this.kycService.kyc.profile.completed = false;
    }
  }

  onEmailValid(valid: boolean) {
    this.kycService.kyc.profile.emailInvalid = valid;
    this.syncUserProfileFromForm();
  }

  bypassEmailValidation() {
    if (this.kycService.isWhatsAppRegister) {
      this.kycService.kyc.profile.emailInvalid = true;
    }
  }

  onSelected(key: string, selected: boolean) {
    const currentScrollPos = document.getElementById('custom-scrollbar');
    switch (key) {
      case 'email':
        if (!this.isWhatsAppRegister) {
          this.scrollToElement(currentScrollPos, this.dob.nativeElement);
          break;
        }
        this.scrollToElement(currentScrollPos, this.gender.nativeElement);
        break;
      case 'gender':
        if (this.isWhatsAppRegister || this.isQuickKyc) {
          this.onClickNext();
          break;
        }
        this.scrollToElement(currentScrollPos, this.marital.nativeElement);
        break;
      case 'marital':
        this.scrollToElementMarital(currentScrollPos, this.smoked.nativeElement);
        break;
      case 'smoked':
        this.onClickNext();
        break;
      default:
        break;
    }
  }

  handleDOBCompleted() {
    const currentScrollPos = document.getElementById('custom-scrollbar');
    if (!this.isWhatsAppRegister) {
      this.scrollToElement(currentScrollPos, this.gender.nativeElement);
      return;
    }
    this.scrollToElement(currentScrollPos, this.marital.nativeElement);
  }

  scrollToElement(parent, element) {
    parent.scrollTo({ top: $(element).position().top, behavior: 'smooth' });
  }

  scrollToElementMarital(parent, element) {
    parent.scrollTo({ top: ($(element).position().top) + 80, behavior: 'smooth' });
  }

  handleEmailClicked(type) {
    const isAndroidDevice = /android/i.test(navigator.userAgent.toLowerCase());
    if (isAndroidDevice) {
      const currentScrollPos = document.getElementById('custom-scrollbar');
      if (type === 'focusin') {
        currentScrollPos.scrollTo({
          top: 150,
          behavior: 'smooth'
        });
      } else {
        this.scrollToElement(currentScrollPos, this.email.nativeElement);
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/features/profile/profile.component.ts